import React, { useState, useEffect, useContext, useCallback } from 'react';
import classNames from 'classnames/bind';
import styles from './PromotionManagement.module.css';
import { ToastContext } from '~/components/context/ToastContext';
import { ModalContext } from '~/components/context/ModalContext';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash, faSave, faSyncAlt, faEdit } from '@fortawesome/free-solid-svg-icons';
import { BASE_GIOVANG_URL } from '~/helper/apiService';

const cx = classNames.bind(styles);

function PromotionManagement() {
  const [promotions, setPromotions] = useState([]);
  const [newPromotionType, setNewPromotionType] = useState('');
  const [newPromotionCount, setNewPromotionCount] = useState(0);
  const [newPromotionDateStart, setNewPromotionDateStart] = useState('');
  const [newPromotionDateEnd, setNewPromotionDateEnd] = useState('');
  const [editingPromotion, setEditingPromotion] = useState(null);
  const [loading, setLoading] = useState(false);
  const [promotionsLoading, setPromotionsLoading] = useState(true);
  const { showToast } = useContext(ToastContext);
  const { showModal } = useContext(ModalContext);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    return new Date(dateString).toLocaleDateString('vi-VN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  };

  // Fetch promotions
  const fetchPromotions = useCallback(async () => {
    try {
      setPromotionsLoading(true);
      const response = await fetch(`${BASE_GIOVANG_URL}promotions`, {
        credentials: 'include',
      });
      const data = await response.json();
      if (response.ok) {
        setPromotions(data.promotions || []);
      } else {
        showToast(data.error || 'Lỗi khi lấy thông tin khuyến mãi', 'error');
      }
    } catch (err) {
      showToast('Lỗi kết nối đến server', 'error');
    } finally {
      setPromotionsLoading(false);
    }
  }, [showToast]);

  // Fetch promotions on mount
  useEffect(() => {
    fetchPromotions();
  }, [fetchPromotions]);

  // Handle adding new promotion
  const handleAddPromotion = useCallback(async () => {
    if (!Number.isInteger(Number(newPromotionCount)) || Number(newPromotionCount) < 0) {
      showToast('Số lượng khuyến mãi phải là số nguyên không âm', 'error');
      return;
    }
    if (!newPromotionType) {
      showToast('Vui lòng nhập loại khuyến mãi', 'error');
      return;
    }
    if (!newPromotionDateStart || !newPromotionDateEnd) {
      showToast('Vui lòng nhập ngày bắt đầu và ngày kết thúc', 'error');
      return;
    }
    if (new Date(newPromotionDateStart) > new Date(newPromotionDateEnd)) {
      showToast('Ngày bắt đầu phải nhỏ hơn hoặc bằng ngày kết thúc', 'error');
      return;
    }

    const tempId = Date.now();
    const newPromotion = {
      id: tempId,
      type: newPromotionType,
      count: Number(newPromotionCount),
      date_start: newPromotionDateStart,
      date_end: newPromotionDateEnd,
    };
    setPromotions((prev) => [...prev, newPromotion]);

    try {
      setLoading(true);
      const response = await fetch(`${BASE_GIOVANG_URL}promotions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          count: Number(newPromotionCount),
          type: newPromotionType,
          date_start: newPromotionDateStart,
          date_end: newPromotionDateEnd,
        }),
      });
      const data = await response.json();
      if (response.ok) {
        showToast('Tạo khuyến mãi thành công', 'success');
        fetchPromotions();
        setNewPromotionType('');
        setNewPromotionCount(0);
        setNewPromotionDateStart('');
        setNewPromotionDateEnd('');
      } else {
        showToast(data.error || 'Lỗi khi tạo khuyến mãi', 'error');
        setPromotions((prev) => prev.filter((prom) => prom.id !== tempId));
      }
    } catch (error) {
      showToast('Lỗi kết nối: ' + error.message, 'error');
      setPromotions((prev) => prev.filter((prom) => prom.id !== tempId));
    } finally {
      setLoading(false);
    }
  }, [newPromotionCount, newPromotionType, newPromotionDateStart, newPromotionDateEnd, showToast, fetchPromotions]);

  // Handle editing promotion
  const handleEditPromotion = useCallback(async (prom) => {
    if (!Number.isInteger(Number(prom.count)) || Number(prom.count) < 0) {
      showToast('Số lượng khuyến mãi phải là số nguyên không âm', 'error');
      return;
    }
    if (!prom.type) {
      showToast('Vui lòng nhập loại khuyến mãi', 'error');
      return;
    }
    if (!prom.date_start || !prom.date_end) {
      showToast('Vui lòng nhập ngày bắt đầu và ngày kết thúc', 'error');
      return;
    }
    if (new Date(prom.date_start) > new Date(prom.date_end)) {
      showToast('Ngày bắt đầu phải nhỏ hơn hoặc bằng ngày kết thúc', 'error');
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(`${BASE_GIOVANG_URL}promotions/${prom.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          count: Number(prom.count),
          type: prom.type,
          date_start: prom.date_start,
          date_end: prom.date_end,
        }),
      });
      const data = await response.json();
      if (response.ok) {
        showToast('Cập nhật khuyến mãi thành công', 'success');
        fetchPromotions();
        setEditingPromotion(null);
      } else {
        showToast(data.error || 'Lỗi khi cập nhật khuyến mãi', 'error');
      }
    } catch (error) {
      showToast('Lỗi kết nối: ' + error.message, 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast, fetchPromotions]);

  // Handle deleting promotion
  const handleDeletePromotion = useCallback(async (id) => {
    const deletedPromotion = promotions.find((prom) => prom.id === id);
    setPromotions((prev) => prev.filter((prom) => prom.id !== id));

    try {
      setLoading(true);
      const response = await fetch(`${BASE_GIOVANG_URL}promotions/${id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      const data = await response.json();
      if (response.ok) {
        showToast('Xóa khuyến mãi thành công', 'success');
        fetchPromotions();
      } else {
        showToast(data.error || 'Lỗi khi xóa khuyến mãi', 'error');
        setPromotions((prev) => [...prev, deletedPromotion]);
      }
    } catch (error) {
      showToast('Lỗi kết nối: ' + error.message, 'error');
      setPromotions((prev) => [...prev, deletedPromotion]);
    } finally {
      setLoading(false);
    }
  }, [promotions, showToast, fetchPromotions]);

  // Confirm delete promotion
  const confirmDeletePromotion = useCallback(
    (prom) => {
      showModal({
        title: 'Xác Nhận Xóa',
        message: `Bạn có chắc chắn muốn xóa khuyến mãi ${prom.type}`,
        onConfirm: () => handleDeletePromotion(prom.id),
        onCancel: null,
      });
    },
    [showModal, handleDeletePromotion]
  );

  // Handle reloading promotion data
  const handleReloadPromotions = useCallback(async () => {
    await fetchPromotions();
    showToast('Tải lại dữ liệu khuyến mãi thành công', 'success');
  }, [fetchPromotions, showToast]);

  return (
    <div className={cx('content')}>
      <div className={cx('redeem-section')}>
        <div className={cx('promotion-form')}>
          <h3 className={cx('section-title')}>
            {editingPromotion ? 'Chỉnh Sửa Khuyến Mãi' : 'Thêm Khuyến Mãi Mới'}
          </h3>
          <div className={cx('form-row')}>
            <label className={cx('form-label')}>Loại:</label>
            <input
              type="text"
              value={editingPromotion ? editingPromotion.type : newPromotionType}
              onChange={(e) =>
                editingPromotion
                  ? setEditingPromotion({ ...editingPromotion, type: e.target.value })
                  : setNewPromotionType(e.target.value)
              }
              className={cx('input')}
              placeholder="Nhập loại khuyến mãi"
            />
          </div>
          <div className={cx('form-row')}>
            <label className={cx('form-label')}>Số Lượng:</label>
            <input
              type="number"
              value={editingPromotion ? editingPromotion.count : newPromotionCount}
              onChange={(e) =>
                editingPromotion
                  ? setEditingPromotion({ ...editingPromotion, count: e.target.value })
                  : setNewPromotionCount(e.target.value)
              }
              className={cx('input')}
              min="0"
              placeholder="Nhập số lượng"
            />
          </div>
          <div className={cx('form-row')}>
            <label className={cx('form-label')}>Ngày Bắt Đầu:</label>
            <input
              type="date"
              value={editingPromotion ? editingPromotion.date_start : newPromotionDateStart}
              onChange={(e) =>
                editingPromotion
                  ? setEditingPromotion({ ...editingPromotion, date_start: e.target.value })
                  : setNewPromotionDateStart(e.target.value)
              }
              className={cx('input')}
              placeholder="Chọn ngày bắt đầu"
            />
          </div>
          <div className={cx('form-row')}>
            <label className={cx('form-label')}>Ngày Kết Thúc:</label>
            <input
              type="date"
              value={editingPromotion ? editingPromotion.date_end : newPromotionDateEnd}
              onChange={(e) =>
                editingPromotion
                  ? setEditingPromotion({ ...editingPromotion, date_end: e.target.value })
                  : setNewPromotionDateEnd(e.target.value)
              }
              className={cx('input')}
              placeholder="Chọn ngày kết thúc"
            />
          </div>
          <button
            onClick={() =>
              editingPromotion ? handleEditPromotion(editingPromotion) : handleAddPromotion()
            }
            disabled={loading}
            className={cx('button', 'save-button')}
          >
            <FontAwesomeIcon icon={faSave} className={cx('button-icon')} />
            <span className={cx('button-text')}>
              {loading ? 'Đang xử lý...' : editingPromotion ? 'Cập Nhật' : 'Thêm Mới'}
            </span>
          </button>
          {editingPromotion && (
            <button
              onClick={() => setEditingPromotion(null)}
              disabled={loading}
              className={cx('button', 'stop-button')}
            >
              <span className={cx('button-text')}>Hủy Chỉnh Sửa</span>
            </button>
          )}
        </div>
        <div className={cx('promotions-list')}>
          <div className={cx('list-header')}>
            <h3 className={cx('section-title')}>Danh Sách Khuyến Mãi</h3>
            <button
              onClick={handleReloadPromotions}
              disabled={loading}
              className={cx('button', 'reload-button')}
            >
              <FontAwesomeIcon icon={faSyncAlt} className={cx('button-icon')} />
              <span className={cx('button-text')}>Load lại dữ liệu</span>
            </button>
          </div>
          {promotionsLoading && promotions.length === 0 ? (
            <p className={cx('loading')}>Đang tải...</p>
          ) : promotions.length === 0 ? (
            <p className={cx('no-data')}>Không có khuyến mãi nào</p>
          ) : (
            promotions.map((prom, index) => (
              <div key={prom.id} className={cx('promotion-card')}>
                <div className={cx('card-info')}>
                  <span className={cx('card-label')}>STT: {index + 1}</span>
                  <span className={cx('card-value')}>Loại: {prom.type}</span>
                  <span className={cx('card-value')}>Số Lượng: {prom.count}</span>
                  <span className={cx('card-value')}>Ngày Bắt Đầu: {formatDate(prom.date_start)}</span>
                  <span className={cx('card-value')}>Ngày Kết Thúc: {formatDate(prom.date_end)}</span>
                </div>
                <div className={cx('card-actions')}>
                  <button
                    onClick={() => setEditingPromotion(prom)}
                    disabled={loading}
                    className={cx('button', 'edit-button')}
                  >
                    <FontAwesomeIcon icon={faEdit} className={cx('button-icon')} />
                    <span className={cx('button-text')}>Sửa</span>
                  </button>
                  <button
                    onClick={() => confirmDeletePromotion(prom)}
                    disabled={loading}
                    className={cx('button', 'stop-button')}
                  >
                    <FontAwesomeIcon icon={faTrash} className={cx('button-icon')} />
                    <span className={cx('button-text')}>Xóa</span>
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

export default PromotionManagement;